import React from "react";
import { useHistory } from "react-router-dom";

import { useMediaQuery } from "@material-ui/core";

import {
  Edit,
  Loading,
  required,
  SimpleForm,
  useEditController,
} from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { FormColumns } from "../../../components/common/FormColumns";
import { FormSection } from "../../../components/common/FormSection";
import {
  ScheduleDateTimeInput,
  useScheduleEventType,
} from "../../../components/common/scheduler/ScheduleDateTimeInput";
import { Workflow } from "../../../utils/constants";
import { getBackPath } from "../../../utils/utils";

import { useOutboundScheduleGuard } from "../dispatchGuard";
import {
  DispatchEditActions,
  DispatchEditToolbar,
} from "./OutboundDispatchEdit";
import { OutboundAside } from "./OutboundAside";
import { ShipmentSetupTab } from "./OutboundShow";

const validateRequired = required();

/**
 * Where a carrier lands immediately after claiming a pickup: the setup fields it
 * just searched on beside the calendar, on one page, so claim-and-schedule
 * stays a single motion — the pickup on the left to check against, the
 * scheduler on the right, no dialog between the carrier and a time. Opening the
 * same pickup from the pickups list gets the full OutboundDispatchEdit instead
 * — CarrierApp picks between the two off the claim's navigation state.
 */
export const OutboundDispatchScheduleEdit = (props) => {
  const { record } = useEditController(props);
  const { appUser } = useUser();
  const history = useHistory();
  const gteMedium = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const backPath = getBackPath(props);
  const { eventType, canSchedule, selectedTimeslot, setSelectedTimeslot } =
    useScheduleEventType(
      record,
      Workflow.OUTBOUND,
      record?.account?.outboundEventTypeId,
    );
  const blocked = useOutboundScheduleGuard(record, props.basePath);

  if (blocked || !record?.account) return <Loading />;
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Schedule Pickup" />}
      mutationMode="pessimistic"
      actions={<DispatchEditActions {...props} backPath={backPath} />}
      aside={<OutboundAside {...props} history={history} backPath={backPath} />}
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <DispatchEditToolbar
            {...props}
            record={record}
            backPath={backPath}
            selectedTimeslot={selectedTimeslot}
          />
        }
      >
        <FormColumns>
          <FormSection title="Outbound Info">
            <ShipmentSetupTab {...props} gteMedium={gteMedium} />
          </FormSection>
          <FormSection title="Schedule Pickup">
            <ScheduleDateTimeInput
              inline
              id="schedule-input"
              label="Pickup Appointment"
              account={record.account}
              workflow={Workflow.OUTBOUND}
              eventType={eventType}
              canSchedule={canSchedule}
              selectedTimeslot={selectedTimeslot}
              setSelectedTimeslot={setSelectedTimeslot}
              validate={validateRequired}
              fullWidth={true}
              helperText={
                canSchedule
                  ? false
                  : `Contact ${appUser?.reloadYard?.name} to setup an event type.`
              }
            />
          </FormSection>
        </FormColumns>
      </SimpleForm>
    </Edit>
  );
};
